import React, { Component } from "react";
import { ExtendedSelectItem, SelectItem } from "../../models/SelectItem";
import { InputText } from "primereact/inputtext";
import { Row, Col, FormGroup, Label } from "reactstrap";
import { AppCard, LoadingDots } from "../../components/ui";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { merchantPaymentMethodApiService } from "../../services/api/merchants/MerchantPaymentMethodApiService";
import { CreateIbanPaymentMethodRequest, MerchantPaymentMethod } from "../../models/MerchantModel";
import { AddBank } from "./AddBank";
import { Banknote, Plus } from "lucide-react";
import { toastService } from "../../services/toastService";
import { utils } from "../../utils/utils";
import { MerchantWalletBalanceRequest, MoneyTrnssferSuccess, Wallets } from "../../models/FinancialData";
import * as Yup from "yup";
import { Amount_Valid_REGEX, AppConstants, Notes_And_Reason_Length, Withdraw_Max_Amount } from "../../models/AppConstants";
import { ErrorMessage, Formik, FormikErrors, FormikHelpers, FormikTouched } from "formik";
import { merchantWalletApiService } from "../../services/api/merchants/MerchantWalletApiService";
import {
    BankTransferInitiateRequestModel,
    BankTransferRequestModel,
    BankTransferResponseCode,
    CalculateMoneyTransferChargeRequest,
    NetworkType,
    PaymentMethodStatus,
    PaymentMethodType,
    PaymentType,
    ServiceClients,
    TennEntityType,
    TransferType,
    WithdrawToAnyIBANMoneyTransferRequest,
    WithdrawToAnyIBANMoneyTransferRequestDetails,
} from "../../models/MoneyTransferModal";
import { InputTextarea } from "primereact/inputtextarea";
import { merchantPaymentApiService } from "../../services/api/merchants/MerchantPaymentApiService";
import WithdrawConfirmDialogue from "./WithdrawConfirmDialogue";
import { OperatorDataModel } from "../../models/User";
import { storageService } from "../../services/StorageService";
import { moneyTransferApiService } from "../../services/api/merchants/MoneyTransferApiService";
import MoneyTransferSuccessDialog from "./MoneyTransferSuccessDialog";
import { KycDocument } from "../../models/DocumentFile";
import { SourceOfFundsUploadRequest } from "../../models/Orchestrator";
import UploadSourceOfFundsDialog from "./UploadSourceOfFundsDialog";

interface WithdrawDialogProps {
    loggedUserData: OperatorDataModel | null;
    sourceAccountsOptions: ExtendedSelectItem[];
    sourceWallets: Wallets[];
    merchantId: number;
    onMoneyTransferSuccess: (model: MoneyTrnssferSuccess) => void;
}

enum CurrentStage {
    Withdraw,
    AddNew,
}

interface WithdrawModal {
    selectSourceAccount: string;
    selectBeneficiaryBank: string;
    amount: string;
    paymentNotes: string;
    selectSourceName: string;
    selectSourceCurrency: string;
    sourceBalance: number;
    selectBeneficiaryName: string;
    beneficiaryCurrency: string;
    beneficiaryCustomerBankName: string;
    networkType: string;
}

interface WithdrawDialogState {
    amount: number;
    isLoading: boolean;
    maxFunds: number;
    currentStage: CurrentStage;
    selectedBank: string | null;
    banks: MerchantPaymentMethod[];
    isSubmitting: boolean;
    btnLoader: boolean;
    beneficiaryPaymentMethodOptions: ExtendedSelectItem[];
    withdrawRequestDetails: WithdrawToAnyIBANMoneyTransferRequestDetails | null;
    sourceOfFundsAreRequired: boolean;
}

const validationSchema = Yup.object().shape({
    selectSourceAccount: Yup.string().required("Please select source account number."),
    selectBeneficiaryBank: Yup.string().required("Please select beneficiary account number"),
    amount: Yup.string()
        .required("Please enter amount.")
        .test("isValidAmount", "Please enter a valid amount, maximum.", (inputAmount) => {
            const amountValue = Number(inputAmount.replace(",", ""));
            return amountValue <= Withdraw_Max_Amount && Amount_Valid_REGEX.test(inputAmount);
        }),
    // .test("isSufficientBalance", "You have insufficient funds in your wallet. Please choose another account.", (inputAmount, { parent }) => {
    //     const amountValue = Number(inputAmount.replace(",", ""));
    //     return amountValue <= parent.sourceBalance;
    // }),
    paymentNotes: Yup.string()
        .required("Please enter payment notes.")
        .max(Notes_And_Reason_Length, "Notes and reasons must not exceed " + Notes_And_Reason_Length + " characters."),
    sourceBalance: Yup.number(),
    selectSourceCurrency: Yup.string(),
    // beneficiaryCurrency: Yup.string()
    //     .test("CurrencyMatch", "Selected Source and Beneficiary Currencies Do No Match", (inputCurrency, { parent }) => {
    //         const SourceCurrency = parent.selectSourceCurrency;
    //         const beneficiaryCurrency = parent.beneficiaryCurrency;
    //         if (SourceCurrency !== beneficiaryCurrency) {
    //             return false;
    //         }
    //         return true;
    //     })
    networkType: Yup.string().required("Please select Network type.")
});

export default class WithdrawComponentDialog extends Component<WithdrawDialogProps, WithdrawDialogState> {
    formikInitialValue: WithdrawModal;
    NetworkTypes: SelectItem[] = [
        {
            label: "BISERA",
            value: NetworkType.Bulk
        },
        {
            label: "INST",
            value: NetworkType.Instant
        },
        {
            label: "VIREMENT",
            value: NetworkType.Virement
        }
    ];

    constructor(props: WithdrawDialogProps | Readonly<WithdrawDialogProps>) {
        super(props);

        (this.state = {
            amount: 0,
            currentStage: CurrentStage.Withdraw,
            isLoading: true,
            banks: [],
            maxFunds: Number.MAX_VALUE,
            selectedBank: null,
            isSubmitting: false,
            btnLoader: false,
            beneficiaryPaymentMethodOptions: [],
            withdrawRequestDetails: null,
            sourceOfFundsAreRequired: false,
        }),
            (this.formikInitialValue = {
                selectSourceAccount: "",
                selectBeneficiaryBank: "",
                amount: "",
                paymentNotes: "",
                selectSourceName: "",
                selectSourceCurrency: "",
                sourceBalance: 0,
                selectBeneficiaryName: "",
                beneficiaryCurrency: "",
                beneficiaryCustomerBankName: "",
                networkType: ""
            });
    }

    componentDidMount(): void {
        this.onLoad();
    }

    componentWillUnmount(): void {
    }

    onLoad = async () => {
        this.setState({ isLoading: true, currentStage: CurrentStage.Withdraw });
        const paymentMethods = await merchantPaymentMethodApiService.getAll(this.props.merchantId);

        if (!paymentMethods.success) {
            this.setState({ isLoading: false });
            toastService.error(paymentMethods.message);
            return;
        }
        const bankPaymentMethodsApproved = paymentMethods.data.filter(
            (a) =>
                (a.status == PaymentMethodStatus.Approve && a.paymentType.toLowerCase() == PaymentMethodType.Bank.toLowerCase()) ||
                (a.status == PaymentMethodStatus.Approve && a.paymentType.toLowerCase() == PaymentMethodType.NonIBAN.toLowerCase()),
        );
        const beneficiaryWalletDropDown = bankPaymentMethodsApproved.map((method) => {
            return {
                label: `${method.ownerName} ${method.value}`,
                value: method.value,
                details: {
                    faicons: "university",
                    fullname: method.ownerName,
                    subtitle: method.bankName,
                    type: method.paymentType.toLocaleLowerCase(),
                },
            } as ExtendedSelectItem;
        });

        this.setState({
            isLoading: false,
            isSubmitting: false,
            banks: bankPaymentMethodsApproved,
            beneficiaryPaymentMethodOptions: beneficiaryWalletDropDown,
        });
    };

    handleSubmit = async (values: WithdrawModal) => {
        this.setState({ btnLoader: true });
        await this.onContinue(values);
        this.setState({ btnLoader: false });
    };

    getWalletBalance = async (accountNumber: string) => {
        const merchantBalanceRequest: MerchantWalletBalanceRequest = {
            merchantId: this.props.merchantId,
            accountNumber: accountNumber,
        };
        const balanceResponse = await merchantWalletApiService.getWalletBalance(merchantBalanceRequest);
        if (!balanceResponse.success) {
            this.setState({ isLoading: false });
            toastService.error(balanceResponse.message);
            return;
        }
        return balanceResponse.data;
    };

    handleSourceAccountChange = async (e: { value: string }, setFieldValue: FormikHelpers<WithdrawModal>["setFieldValue"], values: WithdrawModal) => {
        this.setState((prevState) => ({ ...prevState, isLoading: true }));
        const response = await this.getWalletBalance(this.props.sourceWallets.find((i) => i.ibanAccountNumber === e.value)?.id ?? "");
        if (response == null) {
            this.setState((prevState) => ({ ...prevState, isLoading: false }));
            return;
        }
        setFieldValue("selectSourceCurrency", response?.currency ?? "");
        setFieldValue("sourceBalance", response?.balance ?? 0);
        this.setState((prevState) => ({ ...prevState, isLoading: false }));
    };

    itemTemplate = (option: ExtendedSelectItem) => {
        return (
            <div>
                <div className="text-primary">{option.details?.fullname}</div>
                <small className="text-muted">{option.value}</small>
            </div>
        );
    };

    onSuccess = async (item: CreateIbanPaymentMethodRequest) => {
        this.setState({ isSubmitting: true });
        const result = await merchantPaymentMethodApiService.create(this.props.merchantId, item);

        if (result.success) {
            toastService.success("Account Added.", "Action Completed");
            this.onLoad();
        } else {
            this.setState({ isSubmitting: false });
            toastService.error(result.message);
        }
    };

    onSourceOfFundsUpload = async (documents: KycDocument[]) => {
        if (documents.length === 0) {
            toastService.error("Please select documents.");
            return;
        }

        this.setState({ isSubmitting: true });

        const promises = documents
            .filter((a) => a.objectURL !== "1")
            .map(async (a) => {
                const result = await moneyTransferApiService.uploadSourceOfFunds({
                    sourceEntityType: TennEntityType.Merchant,
                    sourceEntityId: storageService.getLoggedInUserMerchantId(),
                    transferId: this.state.withdrawRequestDetails?.transferId!,
                    fileName: a.fileName,
                    fileData: a.fileData
                } as SourceOfFundsUploadRequest);

                a.objectURL = result.success ? "1" : "";
                return result;
            });

        const results = await Promise.all(promises);
        const failedResults = results.filter((r) => !r.success);

        if (failedResults.length > 0) {
            this.setState({ isSubmitting: false });
            toastService.error("Uploading failed");

            return;
        }

        const submitResult = await moneyTransferApiService.notifySourceOfFundsUploaded({
            sourceEntityType: TennEntityType.Merchant,
            sourceEntityId: storageService.getLoggedInUserMerchantId(),
            transferId: this.state.withdrawRequestDetails?.transferId
        } as BankTransferRequestModel);

        if (!submitResult.success) {
            this.setState({ isSubmitting: false });
            toastService.error(submitResult.message);
            return;
        }

        this.setState({ isSubmitting: false, sourceOfFundsAreRequired: false });
    };

    onContinue = async (data: WithdrawModal) => {
        if (data.selectSourceCurrency != data.beneficiaryCurrency) {
            toastService.error("Source and Beneficiary Currencies Do No Match");
            return;
        }
        // const calculateMoneyTransferChargeRequest: CalculateMoneyTransferChargeRequest = {
        //     customerAccount: data.selectSourceAccount,
        //     amount: Number(data.amount),
        //     id: this.props.loggedUserData?.id ?? 0,
        //     PaymentType: PaymentType.P2IbanWithdrawal,
        // };

        // const calculateMoneyTransferChargeReponse = await merchantPaymentApiService.getMoneyTransferCharge(calculateMoneyTransferChargeRequest);

        // if (!calculateMoneyTransferChargeReponse.success) {
        //     this.setState({ btnLoader: false });
        //     this.toast?.show({ severity: "error", summary: "Error", detail: calculateMoneyTransferChargeReponse.message, life: 3000 });
        //     return;
        // }

        var moneyTransferObject: BankTransferInitiateRequestModel = {
            sourceEntityType: TennEntityType.Merchant,
            transferType: TransferType.Withdrawal,
            requestChannel: ServiceClients.MerchantPayment,
            beneficiaryEntityType: TennEntityType.Individual,
            sourceEntityId: storageService.getLoggedInUserMerchantId(),
            sourceCustomerIban: data.selectSourceAccount,
            beneficiaryCustomerIban: data.selectBeneficiaryBank,
            beneficiaryCustomerBic: "",
            beneficiaryCustomerName: data.selectBeneficiaryName,
            amount: Number(data.amount),
            paymentNotes: data.paymentNotes,
            // currency: data.selectSourceCurrency,
            beneficiaryEntityId: 0,
            network: data.networkType
        };

        const moneyTransferInitiate = await moneyTransferApiService.moneyTransferInititate(moneyTransferObject);
        console.info("After toAnyIbanMoneyTransferInitiateAsync. Result: ", JSON.stringify(moneyTransferInitiate));

        if (!moneyTransferInitiate.success) {
            console.info("toAnyIbanMoneyTransferInitiateAsync Error: ", moneyTransferInitiate.message);
            toastService.error(moneyTransferInitiate.message);
            this.setState({ btnLoader: false });
            return;
        }

        if (moneyTransferInitiate.data.responseCode == BankTransferResponseCode.RiskReject) {
            this.setState(
                { isSubmitting: false },
                () => {
                    toastService.error(moneyTransferInitiate.message ?? "Rejected by Risk Reasons");
                }
            );
            return;
        }
        if (moneyTransferInitiate.data.responseCode == BankTransferResponseCode.UnderReiew) {
            this.setState(
                { isSubmitting: false },
                () => {
                    toastService.error(moneyTransferInitiate.message ?? "Send for evaluation from Compliance.");
                }
            );
            return;
        }

        const transactioDetails: WithdrawToAnyIBANMoneyTransferRequestDetails = {
            sourceCustomerAccount: data.selectSourceAccount,
            sourceCustomerName: data.selectSourceName,
            sourceCustomerCurrency: data.selectSourceCurrency,
            beneficiaryCustomerAccount: data.selectBeneficiaryBank,
            beneficiaryCustomerCurrency: data.beneficiaryCurrency,
            beneficiaryCustomerName: data.selectBeneficiaryName,
            transactionAmount: Number(data.amount),
            paymentNotes: data.paymentNotes,
            transactionFee: moneyTransferInitiate.data.chargeAmount,
            sourceCurrentBalance: data.sourceBalance,
            beneficiaryCustomerBankName: data.beneficiaryCustomerBankName,
            transferId: moneyTransferInitiate.data.transferId,
        };
        this.setState({ withdrawRequestDetails: transactioDetails, btnLoader: false, sourceOfFundsAreRequired: moneyTransferInitiate.data.responseCode === BankTransferResponseCode.SourceOfFundsRequired });
    };

    renderLaoding = () => {
        if (this.state.isLoading) {
            return (
                <LoadingDots />
            );
        }

        if (this.state.banks.length <= 0) {
            return (
                <div className="text-center my-12">
                    <b>No Bank Accounts Available</b>
                </div>
            );
        }
    };

    onSubmitExecute = async () => {
        this.setState({ isSubmitting: true });
        var p2AnyIABNObject: BankTransferRequestModel = {
            sourceEntityType: TennEntityType.Merchant,
            sourceEntityId: storageService.getLoggedInUserMerchantId(),
            transferId: this.state.withdrawRequestDetails?.transferId ?? 0
        };

        const moneyTransferExecuteResponse = await moneyTransferApiService.moneyTransferExecute(p2AnyIABNObject);

        if (!moneyTransferExecuteResponse.success) {
            this.setState({ isSubmitting: false, withdrawRequestDetails: null  });
            toastService.error(moneyTransferExecuteResponse.message);
            return;
        }


        if (moneyTransferExecuteResponse.data.responseCode == BankTransferResponseCode.RiskReject) {
            this.setState(
                { isSubmitting: false, withdrawRequestDetails: null },
                () => {
                    toastService.error(moneyTransferExecuteResponse.message ?? "Rejected by Risk Reasons");
                }
            );
            return;
        }
        if (moneyTransferExecuteResponse.data.responseCode == BankTransferResponseCode.UnderReiew) {
            this.setState(
                { isSubmitting: false, withdrawRequestDetails: null },
                () => {
                    toastService.error(moneyTransferExecuteResponse.message ?? "Send for evaluation from Compliance.");
                }
            );
            return;
        }
        this.setState({ isSubmitting: false, withdrawRequestDetails: null })

        this.props.onMoneyTransferSuccess({
            transactionId: moneyTransferExecuteResponse.data.transactionId,
            isSuccess: true,
        });
    }

    // onSubmit = async () => {
    //     try {
    //         this.setState({ isSubmitting: true });
    //         const toAnyIbanWithdrawalObject: WithdrawToAnyIBANMoneyTransferRequest = {
    //             sourceCustomerAccount: this.state.withdrawRequestDetails?.sourceCustomerAccount!,
    //             beneficiaryCustomerAccount: this.state.withdrawRequestDetails?.beneficiaryCustomerAccount!,
    //             beneficiaryCustomerName: this.state.withdrawRequestDetails?.beneficiaryCustomerName!,
    //             amount: Number(this.state.withdrawRequestDetails?.transactionAmount),
    //             notes: this.state.withdrawRequestDetails?.paymentNotes,
    //             currency: this.state.withdrawRequestDetails?.sourceCustomerCurrency!,
    //         };
    //         const toAnyIbanWithdrawalResponse = await merchantPaymentApiService.toAnyIbanWithdrawal(
    //             this.props.loggedUserData?.id ?? 0,
    //             toAnyIbanWithdrawalObject,
    //         );
    //         if (!toAnyIbanWithdrawalResponse.success) {
    //             this.setState({ isSubmitting: false });
    //             this.toast?.show({ severity: "error", summary: "Error", detail: toAnyIbanWithdrawalResponse.message, life: 3000 });
    //             return;
    //         }
    //         this.setState({ isSubmitting: false, successAlert: true, tID: toAnyIbanWithdrawalResponse.data });
    //     } catch (error) {
    //         console.log("Exception occure in withdraw submitting----", error);
    //         this.setState({ isSubmitting: false });
    //     }
    // };

    render(): React.ReactNode {
        if (this.state.currentStage == CurrentStage.AddNew) {
            return (
                <>
                    <AddBank
                        selectedMerchantId={this.props.merchantId}
                        isSubmitting={this.state.isSubmitting}
                        OnSuccess={this.onSuccess}
                        OnBack={() => this.onLoad()}
                    />
                </>
            );
        }

        return (
            <>
                <AppCard className="p-0 m-0">
                    <AppCard.Body>
                        <div>
                            <Banknote
                                size={80}
                                strokeWidth={1.5}
                                className="text-info block mx-auto"
                            />
                            <Formik
                                validateOnChange={true}
                                validateOnBlur={true}
                                initialValues={this.formikInitialValue}
                                validationSchema={validationSchema}
                                onSubmit={async (values) => {
                                    await this.handleSubmit(values);
                                }}
                            >
                                {({ values, errors, touched, handleSubmit, setFieldValue, handleBlur }) => (
                                    <>
                                        {this.renderLaoding()}
                                        <>
                                            {values.selectSourceCurrency && (
                                                <div className="d-flex justify-content-end align-items-end">
                                                    <p>
                                                        Bal:{" "}
                                                        <span className="text-success">
                                                            {utils.formatBalance(values.sourceBalance, values.selectSourceCurrency)}
                                                        </span>
                                                    </p>
                                                </div>
                                            )}

                                            <Row>
                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select source Account</Label>
                                                        <Dropdown
                                                            appendTo={document.body}
                                                            value={values.selectSourceAccount}
                                                            onChange={(e) => {
                                                                this.handleSourceAccountChange(e, setFieldValue, values);
                                                                setFieldValue("selectSourceAccount", e.value);
                                                                setFieldValue(
                                                                    "selectSourceName",
                                                                    this.props.sourceAccountsOptions.find((i) => i.value == e.value)?.details?.fullname ??
                                                                    "",
                                                                );
                                                            }}
                                                            name="selectSourceAccount"
                                                            options={this.props.sourceAccountsOptions}
                                                            required={true}
                                                            filter
                                                            disabled={this.state.beneficiaryPaymentMethodOptions.length == 0 || this.state.btnLoader}
                                                            className={`w-full ${errors.selectSourceAccount && touched.selectSourceAccount ? "p-invalid" : ""}`}
                                                        />
                                                        <ErrorMessage component="small" name="selectSourceAccount" className="p-error" />
                                                    </FormGroup>
                                                </Col>

                                                <Col md={6}>
                                                    <FormGroup>
                                                        <Label>Select Network</Label>
                                                        <Dropdown
                                                            appendTo={document.body}
                                                            value={values.networkType}
                                                            onChange={(e) => setFieldValue("networkType", e.value)}
                                                            name="networkType"
                                                            options={this.NetworkTypes}
                                                            required={true}
                                                            className={`w-full ${errors.networkType && touched.networkType ? "p-invalid" : ""}`}
                                                            disabled={this.state.beneficiaryPaymentMethodOptions.length == 0 || this.state.btnLoader}
                                                        />
                                                        <ErrorMessage component="small" name="networkType" className="p-error" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>



                                            <Row>
                                                <Col>
                                                    <FormGroup>
                                                        <Label>Select Beneficiary Account</Label>
                                                        <Dropdown
                                                            disabled={
                                                                this.state.beneficiaryPaymentMethodOptions.length == 0 || this.state.btnLoader
                                                            }
                                                            appendTo={document.body}
                                                            value={values.selectBeneficiaryBank}
                                                            onChange={(e) => {
                                                                const selectedBeneficiaryIBAN = this.state.banks.find((i) => i.value === e.value);
                                                                setFieldValue("selectBeneficiaryName", selectedBeneficiaryIBAN?.ownerName ?? "");
                                                                setFieldValue("beneficiaryCurrency", selectedBeneficiaryIBAN?.currency ?? "");
                                                                setFieldValue(
                                                                    "beneficiaryCustomerBankName",
                                                                    selectedBeneficiaryIBAN?.bankName ?? "",
                                                                );
                                                                setFieldValue("selectBeneficiaryBank", e.value);
                                                            }}
                                                            name="selectBeneficiaryBank"
                                                            options={this.state.beneficiaryPaymentMethodOptions}
                                                            required={true}
                                                            filter
                                                            className={`w-full ${errors.selectBeneficiaryBank && touched.selectBeneficiaryBank ? "p-invalid" : ""}`}
                                                            itemTemplate={this.itemTemplate}
                                                        />
                                                        <ErrorMessage component="small" name="selectBeneficiaryBank" className="p-error" />
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <FormGroup>
                                                <Label>Amount</Label>
                                                <InputText
                                                    style={{ backgroundColor: "transparent" }}
                                                    required={true}
                                                    value={values.amount}
                                                    onChange={(inputValue) => {
                                                        const formattedAmount = utils.formatAmount(inputValue.target.value);
                                                        setFieldValue("amount", formattedAmount);
                                                    }}
                                                    disabled={values.selectSourceAccount == "" || this.state.btnLoader}
                                                    name="amount"
                                                    className={`w-full ${errors.amount && touched.amount ? "p-invalid" : ""}`}
                                                    maxLength={10}
                                                />
                                                <ErrorMessage component="small" name="amount" className="p-error" />
                                            </FormGroup>

                                            <FormGroup>
                                                <Label>Payment Notes</Label>
                                                <InputTextarea
                                                    required={true}
                                                    value={values.paymentNotes}
                                                    onChange={(value) => setFieldValue("paymentNotes", value.target.value)}
                                                    readOnly={values.selectSourceAccount == "" || this.state.btnLoader}
                                                    name="paymentNotes"
                                                    className={`w-full ${errors.paymentNotes && touched.paymentNotes ? "p-invalid" : ""}`}
                                                    maxLength={AppConstants.NotesAndReasonLength}
                                                />
                                                <ErrorMessage component="small" name="paymentNotes" className="p-error" />
                                            </FormGroup>
                                        </>

                                        <Row>
                                            <Col xl={{ size: 4, offset: 5 }} lg={{ size: 5, offset: 3 }} md={{ size: 6 }} sm={{ size: 12 }}>
                                                <Button
                                                    className="w-full"
                                                    outlined
                                                    label="Add New Bank"
                                                    type="button"
                                                    icon={<Plus size={16} />}
                                                    onClick={() => this.setState({ currentStage: CurrentStage.AddNew })}
                                                    disabled={this.state.btnLoader}
                                                ></Button>
                                            </Col>
                                            <Col xl={{ size: 3 }} lg={{ size: 4 }} md={{ size: 6 }} sm={{ size: 12 }}>
                                                <Button
                                                    iconPos="right"
                                                    className="w-full"
                                                    label="Continue"
                                                    type="button"
                                                    onClick={() => handleSubmit()}
                                                    loading={this.state.btnLoader}
                                                    disabled={this.state.btnLoader}
                                                    //  onClick={() => this.props.OnSuccess(this.state.amount, this.state.banks.find(a => a.value == this.state.selectedBank))}
                                                ></Button>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Formik>
                        </div>
                    </AppCard.Body>
                </AppCard>


                {this.state.withdrawRequestDetails != null && !this.state.sourceOfFundsAreRequired && (
                    <WithdrawConfirmDialogue
                        withdrawToIBANDetails={this.state.withdrawRequestDetails}
                        isSubmitting={this.state.isSubmitting}
                        onSubmit={this.onSubmitExecute}
                        onHide={() => {
                            this.setState({ isSubmitting: false, withdrawRequestDetails: null });
                        }}
                    />
                )}

                {this.state.withdrawRequestDetails != null && this.state.sourceOfFundsAreRequired && (
                    <UploadSourceOfFundsDialog
                        isSubmitting={this.state.isSubmitting}
                        onSubmit={this.onSourceOfFundsUpload}
                        onHide={() => {
                            this.setState({
                                isSubmitting: false,
                                sourceOfFundsAreRequired: false,
                                withdrawRequestDetails: null
                            });
                        }}
                    />
                )}
            </>
        );
    }
}
